import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../services/api';

interface PayoutErrorLogEntry {
  id: string;
  traceId: string;
  merchantId: string;
  merchantReferenceNumber: string;
  stage: string;
  errorCode: string;
  errorMessage: string;
  sqlMessage: string;
  elapsedMs: string;
  createdAt: string;
}

const PayoutErrorLogs: React.FC = () => {
  const [logs, setLogs] = useState<PayoutErrorLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    merchantId: '',
    traceId: '',
  });

  const loadPayoutErrorLogs = useCallback(async (filterValues: { merchantId: string; traceId: string }) => {
    try {
      setLoading(true);
      const params: { merchant_id?: string; trace_id?: string; limit: number } = { limit: 200 };
      if (filterValues.merchantId.trim()) params.merchant_id = filterValues.merchantId.trim();
      if (filterValues.traceId.trim()) params.trace_id = filterValues.traceId.trim();

      const res = await adminAPI.listPayoutErrorLogs(params);
      const rows = Array.isArray(res?.data?.data) ? res.data.data : [];
      const mapped: PayoutErrorLogEntry[] = rows.map((r: any) => ({
        id: String(r.id),
        traceId: String(r.trace_id || ''),
        merchantId: String(r.merchant_id || ''),
        merchantReferenceNumber: String(r.merchant_reference_number || ''),
        stage: String(r.stage || ''),
        errorCode: String(r.error_code || ''),
        errorMessage: String(r.error_message || ''),
        sqlMessage: String(r.sql_message || ''),
        elapsedMs: r.elapsed_ms != null ? String(r.elapsed_ms) : '',
        createdAt: r.created_at ? new Date(r.created_at).toISOString() : new Date().toISOString(),
      }));
      setLogs(mapped);
    } catch (error) {
      console.error('Failed to load payout error logs:', error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPayoutErrorLogs({ merchantId: '', traceId: '' });
  }, []);

  const filteredLogs = useMemo(() => {
    const merchantIdQuery = filters.merchantId.trim().toLowerCase();
    const traceIdQuery = filters.traceId.trim().toLowerCase();
    return logs.filter(row => {
      const merchantIdMatch = !merchantIdQuery || String(row.merchantId || '').toLowerCase().includes(merchantIdQuery);
      const traceIdMatch = !traceIdQuery || String(row.traceId || '').toLowerCase().includes(traceIdQuery);
      return merchantIdMatch && traceIdMatch;
    });
  }, [logs, filters]);

  return (
    <div className="admin-dashboard-content">
      <div className="admin-section">
        <div className="section-header">
          <div className="section-title-wrapper">
            <h2>📉 Payout Error Logs</h2>
            <span className="section-badge control-badge">DIAGNOSTICS</span>
          </div>
          <p>Recent payout failures captured from merchant submit flow with stage and trace id.</p>
        </div>

        <div className="filters-section" style={{ marginTop: 12 }}>
          <div className="filters-row" style={{ gridTemplateColumns: 'repeat(12, 1fr)' } as React.CSSProperties}>
            <div className="filter-group" style={{ gridColumn: 'span 4' }}>
              <label>Merchant ID</label>
              <input
                className="filter-select"
                placeholder="e.g. 123"
                value={filters.merchantId}
                onChange={e => setFilters(prev => ({ ...prev, merchantId: e.target.value }))}
              />
            </div>
            <div className="filter-group" style={{ gridColumn: 'span 6' }}>
              <label>Trace ID</label>
              <input
                className="filter-select"
                placeholder="e.g. payout_..."
                value={filters.traceId}
                onChange={e => setFilters(prev => ({ ...prev, traceId: e.target.value }))}
              />
            </div>
            <div className="filter-group" style={{ gridColumn: 'span 2', display: 'flex', alignItems: 'flex-end' }}>
              <button type="button" className="filter-button" onClick={() => loadPayoutErrorLogs(filters)} style={{ width: '100%' }}>
                {loading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>
        </div>

        <div className="table-container" style={{ marginTop: 16 }}>
          <table className="transaction-table merchants-table">
            <thead>
              <tr>
                <th>Merchant</th>
                <th>Ref No</th>
                <th>Trace ID</th>
                <th>Stage</th>
                <th>Code</th>
                <th>Error</th>
                <th>ms</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ textAlign: 'center', padding: '14px 12px', color: '#64748b' }}>
                    No payout error logs found.
                  </td>
                </tr>
              ) : (
                filteredLogs.map(row => (
                  <tr key={row.id}>
                    <td>{row.merchantId || '—'}</td>
                    <td>{row.merchantReferenceNumber || '—'}</td>
                    <td>{row.traceId || '—'}</td>
                    <td>{row.stage || '—'}</td>
                    <td>{row.errorCode || '—'}</td>
                    <td>{row.errorMessage || row.sqlMessage || '—'}</td>
                    <td>{row.elapsedMs || '—'}</td>
                    <td>{new Date(row.createdAt).toLocaleString()}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default PayoutErrorLogs;
